import * as React from 'react';
import { NO_HIGHLIGHTED_OPTION_INDEX } from '../hooks/selectState.utils';
import type {
	IOption,
	ISelectGroupRenderModel,
	ISelectOptionRenderState,
	SelectOptionValue,
	SelectSelectionMode,
} from '../types';
import { SelectEmptyState } from './SelectEmptyState';
import { SelectGroupedOptions } from './SelectGroupedOptions';
import { SelectLoadingState } from './SelectLoadingState';
import { Search } from '@components/atoms';
import { SelectOptionButton } from './SelectOptionButton';
import { cn } from '@utils';

/**
 * Interface for the props of the select options list component.
 * @template TValue - The type of the value of the option.
 * @returns {ISelectOptionsListProps} - The props object.
 */
export interface ISelectOptionsListProps<
	TValue extends SelectOptionValue = SelectOptionValue,
	TMetadata = unknown
> extends React.HTMLAttributes<HTMLDivElement> {
	activeDescendantId?: string;
	containerRef?: React.MutableRefObject<HTMLDivElement | null> | React.RefCallback<HTMLDivElement>;
	disableGroupSelectAll?: boolean;
	emptyLabel: string;
	enableGroupSelectAll?: boolean;
	getOptionKey?: (option: IOption<TValue, TMetadata>) => string;
	getGroupSelectAllId: (groupValue: string) => string | undefined;
	getOptionId: (option: IOption<TValue, TMetadata>) => string | undefined;
	groupedOptions?: ReadonlyArray<ISelectGroupRenderModel<TValue, TMetadata>>;
	highlightedGroupSelectAllId?: string;
	highlightedIndex: number;
	isHighlightVisible: boolean;
	lastOptionRef?: (node: Element | null) => void;
	listboxId?: string;
	listboxLabelledBy?: string;
	loading?: boolean;
	loadingMore?: boolean;
	loadingLabel: string;
	onGroupSelect?: (group: ISelectGroupRenderModel<TValue, TMetadata>) => void;
	onOptionSelect: (option: IOption<TValue, TMetadata>) => void;
	onSearchChange?: (value: string) => void;
	options: ReadonlyArray<IOption<TValue, TMetadata>>;
	renderOption?: (option: IOption<TValue, TMetadata>, state: ISelectOptionRenderState) => React.ReactNode;
	searchInputAriaDescribedBy?: React.AriaAttributes['aria-describedby'];
	searchInputAriaInvalid?: React.AriaAttributes['aria-invalid'];
	searchInputAriaLabel?: React.AriaAttributes['aria-label'];
	searchInputAriaLabelledBy?: React.AriaAttributes['aria-labelledby'];
	searchInputAriaRequired?: React.AriaAttributes['aria-required'];
	searchInputHasVisibleLabel?: boolean;
	searchInputId?: string;
	searchPlaceholder: string;
	searchInputRef?: React.Ref<HTMLInputElement>;
	searchValue?: string;
	searchable?: boolean;
	getGroupSelectAllLabel?: (groupLabel: string) => string;
	selectedValues: ReadonlyArray<TValue>;
	selectionMode: SelectSelectionMode;
}

const SCROLL_ALIGNMENT_OPTIONS: ScrollIntoViewOptions = {
	block: 'nearest',
	inline: 'nearest',
};
/**
 * Component for the options list of the select component.
 * @template TValue - The type of the value of the option.
 * @param {ISelectOptionsListProps<TValue>} props - The props of the component.
 * @returns {React.ReactNode} - The options list component.
 */
export const SelectOptionsList = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	activeDescendantId,
	className,
	containerRef,
	disableGroupSelectAll = false,
	emptyLabel,
	enableGroupSelectAll = false,
	getGroupSelectAllLabel,
	getOptionKey,
	getGroupSelectAllId,
	getOptionId,
	groupedOptions,
	highlightedGroupSelectAllId,
	highlightedIndex,
	isHighlightVisible,
	lastOptionRef,
	listboxId,
	listboxLabelledBy,
	loading = false,
	loadingMore = false,
	loadingLabel,
	onGroupSelect,
	onOptionSelect,
	onSearchChange,
	options,
	renderOption,
	searchInputAriaDescribedBy,
	searchInputAriaInvalid,
	searchInputAriaLabel,
	searchInputAriaLabelledBy,
	searchInputAriaRequired,
	searchInputHasVisibleLabel = false,
	searchInputId,
	searchPlaceholder,
	searchInputRef,
	searchValue = '',
	searchable = false,
	selectedValues,
	selectionMode,
	...props
}: ISelectOptionsListProps<TValue, TMetadata>): React.ReactElement => {
	const lastOptionNodeRef = React.useRef<HTMLButtonElement | null>(null);
	const lastOptionRefRef = React.useRef(lastOptionRef);
	const highlightedOptionRef = React.useRef<HTMLButtonElement | null>(null);
	const highlightedGroupHeaderRef = React.useRef<HTMLDivElement | null>(null);
	const hasProcessedInitialScrollRef = React.useRef(false);
	const wereOptionsRenderedRef = React.useRef(false);
	const hasGroupedOptions = groupedOptions !== undefined;
	const hasOptions = options.length > 0;
	const selectedOptionIndex = React.useMemo(
		() => options.findIndex(option => selectedValues.includes(option.value)),
		[options, selectedValues]
	);
	const scrollTargetIndex =
		highlightedIndex === NO_HIGHLIGHTED_OPTION_INDEX ? selectedOptionIndex : highlightedIndex;

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		onSearchChange?.(event.target.value);
	};

	React.useEffect(() => {
		const isInitialScroll = !hasProcessedInitialScrollRef.current;
		const areOptionsRendered = !loading && hasOptions;
		const hasNewlyRenderedScrollTarget =
			!wereOptionsRenderedRef.current &&
			areOptionsRendered &&
			scrollTargetIndex !== NO_HIGHLIGHTED_OPTION_INDEX;

		hasProcessedInitialScrollRef.current = true;
		wereOptionsRenderedRef.current = areOptionsRendered;

		if (
			highlightedIndex === NO_HIGHLIGHTED_OPTION_INDEX &&
			!isInitialScroll &&
			!hasNewlyRenderedScrollTarget
		) {
			return;
		}

		if (highlightedGroupSelectAllId) {
			return;
		}

		const scrollTargetNode =
			scrollTargetIndex === NO_HIGHLIGHTED_OPTION_INDEX
				? null
				: (highlightedOptionRef.current ?? lastOptionNodeRef.current);
		scrollTargetNode?.scrollIntoView(SCROLL_ALIGNMENT_OPTIONS);
		highlightedGroupHeaderRef.current?.scrollIntoView(SCROLL_ALIGNMENT_OPTIONS);
	}, [
		hasOptions,
		highlightedGroupSelectAllId,
		highlightedIndex,
		isHighlightVisible,
		loading,
		scrollTargetIndex,
	]);

	React.useEffect(() => {
		const previousLastOptionRef = lastOptionRefRef.current;
		const lastOptionNode = lastOptionNodeRef.current;
		lastOptionRefRef.current = lastOptionRef;

		if (!lastOptionNode || previousLastOptionRef === lastOptionRef) {
			return;
		}

		previousLastOptionRef?.(null);
		lastOptionRef?.(lastOptionNode);
	}, [lastOptionRef]);

	const handleLastOptionRef = React.useCallback((node: HTMLButtonElement | null) => {
		lastOptionNodeRef.current = node;
		lastOptionRefRef.current?.(node);
	}, []);

	const renderOptionButton = (
		option: IOption<TValue, TMetadata>,
		index: number
	): React.ReactElement => {
		const shouldTrackHighlightedOption = scrollTargetIndex === index;
		const shouldTrackLastOption = index === options.length - 1;

		const optionRef = shouldTrackLastOption
			? handleLastOptionRef
			: shouldTrackHighlightedOption
				? highlightedOptionRef
				: undefined;

		return (
			<SelectOptionButton
				getOptionId={getOptionId}
				highlighted={isHighlightVisible && highlightedIndex === index}
				key={getOptionKey?.(option) ?? String(option.value)}
				onOptionSelect={onOptionSelect}
				option={option}
				optionRef={optionRef}
				renderOption={renderOption}
				selected={selectedValues.includes(option.value)}
				selectionMode={selectionMode}
			/>
		);
	};

	const renderOptionsContent = (): React.ReactNode => {
		if (hasGroupedOptions && groupedOptions) {
			return (
				<SelectGroupedOptions
					disableGroupSelectAll={disableGroupSelectAll}
					emptyLabel={emptyLabel}
					enableGroupSelectAll={enableGroupSelectAll}
					getGroupSelectAllLabel={getGroupSelectAllLabel}
					getGroupSelectAllId={getGroupSelectAllId}
					groupedOptions={groupedOptions}
					highlightedGroupSelectAllId={highlightedGroupSelectAllId}
					onGroupSelect={onGroupSelect}
					options={options}
					renderOptionButton={renderOptionButton}
					scrollTargetGroupHeaderRef={highlightedGroupHeaderRef}
					scrollTargetIndex={scrollTargetIndex}
					selectionMode={selectionMode}
				/>
			);
		}

		return options.map(renderOptionButton);
	};

	const renderOptions = (): React.ReactNode => (
		<>
			{renderOptionsContent()}
			{loadingMore && !loading && <SelectLoadingState label={loadingLabel} />}
		</>
	);

	return (
		<div
			{...props}
			className={cn(
				'absolute left-0 z-50 mt-1 flex max-h-96 w-full flex-col overflow-hidden rounded-xl border border-borderPrimary bg-white p-1.5 shadow-lg',
				className
			)}
			data-testid="select-options-list"
			ref={element => {
				if (typeof containerRef === 'function') {
					containerRef(element);
				} else if (containerRef) {
					containerRef.current = element;
				}
			}}
		>
			{searchable && (
				<div className="flex shrink-0 items-center gap-2 bg-white px-3 py-2">
					<Search aria-hidden="true" className="h-4 w-4 shrink-0 text-secondary" />
					<input
						aria-activedescendant={activeDescendantId}
						aria-autocomplete="list"
						aria-controls={listboxId}
						aria-describedby={searchInputAriaDescribedBy}
						aria-expanded="true"
						aria-haspopup="listbox"
						aria-invalid={searchInputAriaInvalid}
						aria-label={
							searchInputAriaLabelledBy || searchInputHasVisibleLabel
								? searchInputAriaLabel
								: searchInputAriaLabel ?? searchPlaceholder
						}
						aria-labelledby={searchInputAriaLabelledBy}
						aria-required={searchInputAriaRequired}
						autoComplete="off"
						className="min-w-0 flex-1 bg-transparent text-xs text-primary outline-hidden placeholder:text-secondary"
						data-testid="select-search-input"
						id={searchInputId}
						onChange={handleSearchChange}
						placeholder={searchPlaceholder}
						ref={searchInputRef}
						role="combobox"
						type="text"
						value={searchValue}
					/>
				</div>
			)}
			<div className="min-h-0 overflow-y-auto" data-testid="select-options-scroll-container">
        <div
          aria-busy={loading || loadingMore}
          aria-labelledby={listboxLabelledBy}
          aria-multiselectable={selectionMode === 'multiple' ? true : undefined}
          className="space-y-1"
          id={listboxId}
          role="listbox"
        >
          {!loading && hasOptions && renderOptions()}
        </div>
        {loading && <SelectLoadingState label={loadingLabel} />}
        {!loading && !hasOptions && <SelectEmptyState label={emptyLabel} />}
      </div>
		</div>
	);
};
